import { ReactNode } from "react";
import clsx from "clsx";

export function Field({
  label,
  sub,
  optional,
  error,
  children,
  htmlFor,
}: {
  label: string;
  sub?: string;
  /** Tag the field as Optional. Defaults to required (no tag). */
  optional?: boolean;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div>
      <label className="ac-label flex items-baseline gap-2" htmlFor={htmlFor}>
        <span>{label}</span>
        {optional && (
          <span className="text-[11px] uppercase tracking-wider font-semibold text-brand-mute/80">
            Optional
          </span>
        )}
      </label>
      {sub && <p className="ac-sub">{sub}</p>}
      <div className="mt-2">{children}</div>
      {error && <p className="ac-error">{error}</p>}
    </div>
  );
}

export function ChipGroup<T extends string | number>({
  options,
  value,
  onChange,
  multi = false,
  columns = 3,
}: {
  options: readonly T[];
  value: T[] | T | undefined;
  onChange: (next: T[] | T) => void;
  multi?: boolean;
  columns?: number;
}) {
  const active = (o: T) =>
    multi
      ? Array.isArray(value) && value.includes(o)
      : value === o;
  return (
    <div
      className="grid gap-2"
      style={{
        gridTemplateColumns: `repeat(auto-fill, minmax(${columns === 5 ? "64px" : "180px"}, 1fr))`,
      }}
    >
      {options.map((o) => (
        <button
          key={String(o)}
          type="button"
          onClick={() => {
            if (multi) {
              const arr = Array.isArray(value) ? value : [];
              const next = active(o)
                ? arr.filter((x) => x !== o)
                : [...arr, o];
              onChange(next);
            } else {
              onChange(o);
            }
          }}
          className={clsx("ac-chip text-center justify-center", active(o) && "ac-chip-active")}
        >
          {String(o)}
        </button>
      ))}
    </div>
  );
}

export function StarRating({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} of 5`}
          onClick={() => onChange(n)}
          className={clsx("ac-star", value !== undefined && n <= value && "ac-star-active")}
        >
          {value !== undefined && n <= value ? "★" : "☆"}
        </button>
      ))}
      <span className="text-[13px] text-brand-mute ml-2">
        {value ? `${value} / 5` : "Pick a rating"}
      </span>
    </div>
  );
}
